import React, { createContext, useState, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getOptions, insertOptions, getOptionsByKey } from "../services/defaultValuesService";
import { setRsplListFromOptions } from "../config";
import i18n from "../i18n";
// Create the context
const OptionsContext = createContext();

// Custom hook to use the context
export const useOptions = () => useContext(OptionsContext);

const OptionsProvider = ({ children }) => {
	const navigate = useNavigate();
	const [options, setOptions] = useState(null);
	const [optionKeys, setOptionKeys] = useState(null);
	const [selectedTheme, setSelectedTheme] = useState(null);
	const [mfaDays, setMfaDays] = useState(null);
	const [mfaEnabled, setMfaEnabled] = useState(null);
	const [optionsLoading, setOptionsLoading] = useState(true);
	const [optionsLoaded, setOptionsLoaded] = useState(false);
	const [optionsError, setOptionsError] = useState(null);
	const supportedLanguages = ["en", "fr", "es", "de"];

	const convertBoolean = (value) => value === "true";

	/**
	 * Load every option row into context.
	 *
	 * Reports outcome through `optionsLoaded` / `optionsError` as well as
	 * `optionsLoading`, because "finished loading" and "actually has the stored
	 * values" used to be the same flag: a failed fetch was swallowed here and
	 * `optionsLoading` still went false, so Settings rendered its blank initial
	 * defaults as if they were the saved configuration and the next Save wrote
	 * those blanks over real production data. Consumers must gate on
	 * `optionsLoaded`, not on `!optionsLoading`.
	 *
	 * @returns {Promise<void>} Resolves once the request settles; never rejects.
	 */
	const fetchOptions = async () => {

		setOptionsLoading(true);
		setOptionsError(null);
		try {
			const response = await getOptions();


			if (response.status) {
			const opts = response.data.options; // this is an object
			setOptionKeys(opts); // save the whole object
			setOptions(response.data);
			setRsplListFromOptions(opts?.rsplList);

			// Access properties directly
			setMfaDays(parseInt(opts.mfaDays) || 15);
			setMfaEnabled(opts.mfaEnabled === "Enabled"); // boolean
			setSelectedTheme(opts.accentColor || opts.theme || "blue");
			setOptionsLoaded(true);
			} else {
			setOptionsLoaded(false);
			setOptionsError(response?.message || "Failed to load settings");
			}
		} catch (error) {
			setOptionsLoaded(false);
			setOptionsError(error?.message || "Failed to load settings");
		} finally {
			setOptionsLoading(false);
		}
		};

	const setOption = async (options) => {
		try {
			const resp = await insertOptions({options});
			if (resp.status) {
				
			}
		} catch (error) {
			
		}
	};

	const getOption = async (option_key) => {
		try {
			const resp = await getOptionsByKey(option_key);
			if (resp.status) {
				
			}
		} catch (error) {
			
		}
	};

	useEffect(() => {
		fetchOptions();
	}, []);

	useEffect(() => {
		if (!optionKeys) return;
		const fallbackLanguage = supportedLanguages.includes(optionKeys.defaultLanguage)
			? optionKeys.defaultLanguage
			: "en";
		// `enableLanguageTranslation` gates ONLY whether the manual language dropdown renders
		// (see showLanguageSelector in Footer.js / Layout2 Footer.js / FooterYAV.js / Language.js)
		// — it must NOT also override what language is actually ACTIVE. i18n.js's LanguageDetector
		// already resolves the browser's own language (navigator.language) via its
		// localStorage-then-navigator detection order; this effect's only job is to correct an
		// UNSUPPORTED detected/cached value (e.g. a browser locale we have no translations for) to
		// a sane fallback. Live-reported 2026-08-07: the previous "!translationEnabled" branch
		// force-called changeLanguage(fallbackLanguage) unconditionally whenever the manual
		// selector was disabled (the deployed default here), permanently pinning every user to one
		// fixed language and defeating browser-language auto-detection entirely — this environment
		// intentionally has NO manual toggle and relies on browser-language detection alone, so
		// that override broke the only language mechanism this deployment actually uses (and, by
		// extension, kept CHIP's screen_context.language — sourced from the same i18n.language —
		// stuck on the fixed fallback too).
		//
		// Real browsers report a REGION-qualified locale (navigator.language is "fr-FR"/"en-US",
		// not bare "fr"/"en") — confirmed live: i18next's OWN t() lookups already fall back to the
		// base-language resource bundle for these (t() with lng="fr-FR" and only an "fr" bundle
		// loaded correctly returns the French string), but i18n.language itself stays the raw,
		// UNREDUCED tag; i18n.resolvedLanguage is the one already reduced to the base code that
		// matches this app's resource keys ("fr"). Checking supportedLanguages against the raw
		// i18n.language here would treat "fr-FR" as unsupported and force EVERY region-tagged
		// browser locale (nearly all real users) back to fallbackLanguage on every load, silently
		// reintroducing the exact "always English" bug this effect was just fixed to remove.
		const effectiveLanguage = i18n.resolvedLanguage || i18n.language;
		if (!supportedLanguages.includes(effectiveLanguage)) {
			i18n.changeLanguage(fallbackLanguage);
		}
	}, [optionKeys]);

	return (
		<OptionsContext.Provider
			value={{
				options,
				optionKeys,
				optionsLoading,
				optionsLoaded,
				optionsError,
				selectedTheme,
				mfaDays,
				mfaEnabled,
				setOption,
				getOption,
				fetchOptions
			}}>
			{children}
		</OptionsContext.Provider>
	);
};

export default OptionsProvider;
